import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, forwardRef, Provider } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ControlValueAccessor, FormControl } from '@angular/forms';
import { ProfileSearchService } from '../search/profile-search.service';
import { ProfileService } from '../shared/profile.service';
import { ProfileRead } from '../shared/profile-read.model';
import { ProfileMin } from '../shared/profile-min.model';
import { ProfileSearchParams } from '../search/profile-search-params.model';
import { ProfileSearchResult } from '../search/profile-search-result.model';
import { TranslateService } from '@ngx-translate/core';
import { UtilService } from '../../shared/util/util.service';
import {
  NumberValueAccessor,
  CreateNumberValueAccessorProvider,
  CreateNumberValueValidationProvider,
} from '../../shared/widgets/value-accessors/number-value-accessor';

@Component({
  selector: 'wl-profile-selector',
  templateUrl: 'profile-selector.component.html',
  styleUrls: ['profile-selector.component.css'],
  providers: [
    ProfileSearchService,
    ProfileService,
    CreateNumberValueAccessorProvider(ProfileSelectorComponent),
    CreateNumberValueValidationProvider(ProfileSelectorComponent),
  ],
  standalone: false
})
export class ProfileSelectorComponent extends NumberValueAccessor {
  @Input() includeNoneValue: boolean = false;
  @Input() noneValueDisplay: string = '';
  @Input() searchParams: ProfileSearchParams;
  @Input() class: string = 'form-control';
  @Input() placeholder: string = '';
  @Input() loadMinProfile: boolean = true;

  //for validation to know if 0 an be allowed
  @Input() required: any;

  @Output() onProfileChanged = new EventEmitter<ProfileMin | ProfileRead>();

  private _lastSearchKeyword: string = '';
  private _lastSearchPageNumber: number = -1;
  private _currentSearchPageNumber: number = 0;
  private _totalSearchResults: number = -1;

  public _profileSearchService: ProfileSearchService;
  public _profileService: ProfileService;
  public _translateService: TranslateService;
  public _utilService: UtilService;
  public _noneText: string;
  public _lastValue: number;

  //row height (px) for the autocomplete virtual scroller; must fit the two-line result template
  public readonly itemSize: number = 60;

  public profiles: Array<ProfileMin>;

  public selectedProfile: ProfileMin;
  public displayName: string;
  public isSearching: boolean;

  public constructor(
    profileSearchService: ProfileSearchService,
    profileService: ProfileService,
    translateService: TranslateService,
    utilService: UtilService
  ) {
    super();
    this._profileSearchService = profileSearchService;
    this._profileService = profileService;
    this._translateService = translateService;
    this._utilService = utilService;
    this._noneText = '';

    this.profiles = new Array<ProfileMin>();

    this.searchParams = new ProfileSearchParams();
    this.selectedProfile = new ProfileMin();

    this._lastValue = -1;
    this.displayName = '';
    this.isSearching = false;

    this._translateService.get(['profiles.selector.search-for-profiles']).subscribe((x) => {
      if (!this.placeholder) this.placeholder = x['profiles.selector.search-for-profiles'];
    });
  }

  //this overrides the base class function to set
  //what it means for this this control to be valid
  isValid = (): boolean => {
    if (this._utilService.isNotNullOrUndefined(this.required)) {
      if (this.value > 0) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    this.emitOutputObject();
  }

  /**
   * only called initially when control is created and ngModel is bound. should not trigger change event.
   * @param v
   */
  public writeValue(v: number): void {
    super.writeValue(v);
    if (this.value > 0) {
      this._profileService.getProfileMin(this.value).then((data: ProfileMin) => {
        this.selectedProfile = data;
        this.setDisplayName();
      });
    } else {
      this.selectedProfile = new ProfileMin();
      this.setDisplayName();
    }
  }

  modelChange() {
    this.emitOutputObject();
  }

  loadProfiles = ($event: any): void => {
    if (this._utilService.isNotNullOrUndefined($event)) {
      if (this._utilService.isNotNullOrUndefined($event.query)) {
        this.searchParams.Keyword = $event.query;
      }
    }

    if (this.searchParams.Keyword.length >= 3 || this._utilService.isNumeric(this.searchParams.Keyword)) {
      this.searchParams.PageSize = 50;
      this.isSearching = true;
      this.searchParams.OrderBy = 'ASF:asc';
      //set back to page 1
      if (this._lastSearchKeyword != this.searchParams.Keyword) {
        this.searchParams.PageNumber = 1;
      }

      this._profileSearchService.search(this.searchParams).then((data: ProfileSearchResult) => {
        this._totalSearchResults = data.TotalCount;
        this._currentSearchPageNumber = data.CurrentPageNumber;

        let existingProfiles = this.profiles;
        this.profiles = new Array<ProfileMin>();

        if (this._lastSearchKeyword == this.searchParams.Keyword) {
          for (let existingProfile of existingProfiles) {
            this.profiles.push(existingProfile);
          }
        }

        this._translateService.get(['common.none']).subscribe((x) => {
          if (this.includeNoneValue == true) {
            if (this.hasProfile(0) == false) {
              let noneProfile = new ProfileMin();
              noneProfile.ProfileId = 0;
              noneProfile.ReportName = x['common.none'];
              this.profiles.push(noneProfile);
            }
          }

          for (let profile of data.Result) {
            if (this.hasProfile(profile.ProfileId) == false) {
              this.profiles.push(profile);
            }
          }

          this._lastSearchKeyword = this.searchParams.Keyword;
          this._lastSearchPageNumber = this.searchParams.PageNumber;

          this.isSearching = false;
        });
      });
    }
  };

  public hasProfile = (profileId: number): boolean => {
    let bol = false;
    for (let profile of this.profiles) {
      if (profile.ProfileId == profileId) {
        bol = true;
        return bol;
      }
    }

    return bol;
  };

  public dropdownProfileSelected = ($event: any) => {
    this.value = $event.value.ProfileId;
    this.displayName = $event.value.ReportName + ' (' + $event.value.ProfileId.toString() + ')';
    this.emitOutputObject();
  };

  emitOutputObject = (): void => {
    if (this.value > 0) {
      let loadProfilePromise: Promise<ProfileMin | ProfileRead> = null;

      if (this.loadMinProfile) loadProfilePromise = this._profileService.getProfileMin(this.value);
      else loadProfilePromise = this._profileService.getProfile(this.value);

      loadProfilePromise.then((data: ProfileMin | ProfileRead) => {
        this.selectedProfile = <ProfileMin>data;
        this.setDisplayName();
        this.onProfileChanged.emit(data);
      });
    } else {
      this.selectedProfile = new ProfileMin();
      this.setDisplayName();
      this.onProfileChanged.emit(this.loadMinProfile ? this.selectedProfile : new ProfileRead());
    }
  };

  public setDisplayName = (): void => {
    if (
      this.selectedProfile &&
      this.selectedProfile.ProfileId > 0 &&
      this._utilService.isNotNullOrUndefined(this.selectedProfile.ReportName) &&
      this._utilService.isNotNullOrUndefined(this.selectedProfile.ProfileId)
    ) {
      this.displayName = this.selectedProfile.ReportName + ' (' + this.selectedProfile.ProfileId.toString() + ')';
    } else {
      this.displayName = '';
    }
  };

  public setNone = (): void => {
    this.value = 0;
    this.displayName = '';
    this.emitOutputObject();
  };

  public onScroll = (): void => {
    if (this.hasMoreSearchResults() && this.isSearching == false) {
      this.searchParams.PageNumber = this.searchParams.PageNumber + 1;
      this.loadProfiles(null);
    }
  };

  /**
   * the virtual scroller reports the range it is rendering; once that range
   * reaches the end of the loaded profiles, fetch the next page
   * @param $event
   */
  public onLazyLoad = ($event: { first: number; last: number }): void => {
    if (this._utilService.isNotNullOrUndefined($event) && $event.last >= this.profiles.length) {
      this.onScroll();
    }
  };

  public hasMoreSearchResults = (): boolean => {
    let bol = false;
    if (this.profiles.length < this._totalSearchResults) {
      bol = true;
    }
    return bol;
  };
}
